import { useState, useRef, useEffect } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";

import SUV from "../../assets/models/scene.glb";

// Componente 3D interativo de carro
// Permite selecionar/desselecionar partes do carro
// Props:
// - containerWidth: largura do canvas
// - containerHeight: altura do canvas
// - defaultValue: nome da parte selecionada inicialmente (string)
// - onChange: callback quando seleciona/deseleciona parte - recebe array com string [partName] ou array vazio []
// - partsAsFilter: true = pode selecionar múltiplas partes, false = apenas uma por vez
// NOTA: O ModalAddParts espera receber array e pega o primeiro elemento [0]

const CarComponent = ({
  containerWidth,
  containerHeight,
  defaultValue,
  onChange,
  partsAsFilter = false,
}) => {
  const containerRef = useRef(null);
  const sceneRef = useRef(null);
  // Para partsAsFilter=true, usar array. Para false, usar string única
  const selectedPartRef = useRef(
    partsAsFilter
      ? (Array.isArray(defaultValue) ? defaultValue : (defaultValue ? [defaultValue] : []))
      : (defaultValue || null)
  );
  const meshesRef = useRef([]);

  // Função auxiliar: normalizar string removendo acentos para comparação
  const normalizeString = (str) => {
    if (!str) return '';
    return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  };

  useEffect(() => {
    if (!containerRef.current) return;

    // ========== CONFIGURAÇÃO DA CENA ==========
    const container = containerRef.current;

    // Renderer
    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
    });
    renderer.setSize(containerWidth, containerHeight);
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1;
    renderer.setClearColor(0xffffff, 0);
    renderer.shadowMap.enabled = true;

    container.appendChild(renderer.domElement);

    // Scene
    const scene = new THREE.Scene();
    sceneRef.current = scene;

    // Camera
    const camera = new THREE.PerspectiveCamera(
      7,
      containerWidth / containerHeight,
      1,
      10000
    );
    camera.position.set(60, 64, 80);

    // Controls
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.update();

    // Lighting
    const light = new THREE.SpotLight(0xffffff);
    light.intensity = 30;
    light.position.set(0, 0, 10);
    scene.add(light);

    const ambient = new THREE.AmbientLight(0xffffff);
    ambient.intensity = 1;
    scene.add(ambient);

    // ========== CARREGAMENTO DO MODELO ==========
    const COLOR_SELECTED = 0xa90c0c;
    const COLOR_HOVERED = 0x00aaff;
    let hoveredGroup = null;

    const loader = new GLTFLoader();
    loader.load(SUV, function (gltf) {
      // Coletar todos os meshes e salvar cor original
      gltf.scene.traverse(function (child) {
        if (child.isMesh) {
          meshesRef.current.push(child);
          child.userData.originalColor = child.material.color.clone();
        }
      });

      // Se tem defaultValue, pintar de vermelho
      if (defaultValue) {
        if (partsAsFilter && Array.isArray(defaultValue)) {
          // Modo multi: pintar todas as peças do array
          meshesRef.current.forEach((mesh) => {
            const normalizedMeshName = normalizeString(mesh.parent.name);
            const isSelected = defaultValue.some(
              part => normalizeString(part) === normalizedMeshName
            );
            if (isSelected) {
              mesh.material.color.set(COLOR_SELECTED);
            }
          });
        } else {
          // Modo single: pintar apenas uma peça
          const normalizedDefault = normalizeString(defaultValue);
          meshesRef.current.forEach((mesh) => {
            const normalizedMeshName = normalizeString(mesh.parent.name);
            if (normalizedMeshName === normalizedDefault) {
              mesh.material.color.set(COLOR_SELECTED);
            }
          });
        }
      }

      scene.add(gltf.scene);
      renderer.render(scene, camera);
    });

    // ========== RAYCASTING E INTERAÇÃO ==========
    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2();

    // Função auxiliar: pegar todos os meshes do mesmo grupo
    function getGroupMeshes(mesh) {
      return mesh.parent.children.filter((child) => child.isMesh);
    }

    // Função auxiliar: verificar se grupo está selecionado (comparação sem acento)
    function isGroupSelected(groupName) {
      if (partsAsFilter) {
        // Modo multi: verificar se está no array
        const selected = selectedPartRef.current;
        if (!Array.isArray(selected)) {
          return false;
        }
        return selected.some(
          part => normalizeString(part) === normalizeString(groupName)
        );
      } else {
        // Modo single: comparação direta
        return normalizeString(selectedPartRef.current) === normalizeString(groupName);
      }
    }

    // Função auxiliar: resetar cores de hover
    function resetHover() {
      if (hoveredGroup) {
        const groupMeshes = getGroupMeshes(hoveredGroup);
        groupMeshes.forEach((mesh) => {
          if (!isGroupSelected(mesh.parent.name)) {
            mesh.material.color.copy(mesh.userData.originalColor);
          }
        });
        hoveredGroup = null;
      }
    }

    // Mouse Move - Hover effect
    function onMouseMove(event) {
      const rect = renderer.domElement.getBoundingClientRect();
      mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

      raycaster.setFromCamera(mouse, camera);
      const intersects = raycaster.intersectObjects(meshesRef.current, false);

      if (intersects.length > 0) {
        const clickedMesh = intersects[0].object;
        const groupName = clickedMesh.parent.name;

        // Se mudou o grupo em hover
        if (hoveredGroup !== clickedMesh) {
          // Resetar hover anterior
          resetHover();

          // IMPORTANTE: Garantir que apenas as peças selecionadas estão vermelhas
          meshesRef.current.forEach((mesh) => {
            if (isGroupSelected(mesh.parent.name)) {
              mesh.material.color.set(COLOR_SELECTED);
            } else {
              mesh.material.color.copy(mesh.userData.originalColor);
            }
          });

          // Aplicar hover apenas se não estiver selecionado
          if (!isGroupSelected(groupName)) {
            hoveredGroup = clickedMesh;
            const groupMeshes = getGroupMeshes(clickedMesh);
            groupMeshes.forEach((mesh) => {
              mesh.material.color.set(COLOR_HOVERED);
            });
          }
        }
      } else {
        resetHover();
        // Garantir que apenas as peças selecionadas estão vermelhas quando não há hover
        meshesRef.current.forEach((mesh) => {
          if (isGroupSelected(mesh.parent.name)) {
            mesh.material.color.set(COLOR_SELECTED);
          } else {
            mesh.material.color.copy(mesh.userData.originalColor);
          }
        });
      }

      renderer.render(scene, camera);
    }

    // Mouse Click - Selecionar/Desselecionar
    function onMouseClick(event) {
      const rect = renderer.domElement.getBoundingClientRect();
      mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

      raycaster.setFromCamera(mouse, camera);
      const intersects = raycaster.intersectObjects(meshesRef.current, false);

      if (intersects.length === 0) return;

      const clickedMesh = intersects[0].object;
      const groupName = clickedMesh.parent.name;
      const groupMeshes = getGroupMeshes(clickedMesh);

      if (!partsAsFilter) {
        // MODO SINGLE: apenas uma peça por vez
        if (isGroupSelected(groupName)) {
          // Clicou na mesma peça: DESSELECIONAR
          groupMeshes.forEach((mesh) => {
            mesh.material.color.copy(mesh.userData.originalColor);
          });
          selectedPartRef.current = null;
          onChange([null]); // Envia [null] ao invés de [] para manter consistência com o formato esperado
        } else {
          // Clicou em outra peça: desselecionar TODAS primeiro
          // Resetar TODAS as cores primeiro (força render para garantir atualização visual)
          meshesRef.current.forEach((mesh) => {
            mesh.material.color.copy(mesh.userData.originalColor);
            mesh.material.needsUpdate = true;
          });

          // Forçar render após reset
          renderer.render(scene, camera);

          // Selecionar nova
          groupMeshes.forEach((mesh) => {
            mesh.material.color.set(COLOR_SELECTED);
            mesh.material.needsUpdate = true;
          });
          // Salvar o nome normalizado (sem acento) no estado
          selectedPartRef.current = normalizeString(groupName);
          onChange([normalizeString(groupName)]);
        }
      } else {
        // MODO MULTI: múltiplas peças podem ser selecionadas
        // Garantir que selectedPartRef.current é sempre um array
        if (!Array.isArray(selectedPartRef.current)) {
          selectedPartRef.current = selectedPartRef.current ? [selectedPartRef.current] : [];
        }

        if (isGroupSelected(groupName)) {
          // Desselecionar esta peça (remover do array)
          groupMeshes.forEach((mesh) => {
            mesh.material.color.copy(mesh.userData.originalColor);
            mesh.material.needsUpdate = true;
          });

          const updatedSelection = selectedPartRef.current.filter(
            part => normalizeString(part) !== normalizeString(groupName)
          );
          selectedPartRef.current = updatedSelection;
          onChange([groupName]); // Envia apenas a peça clicada (compatibilidade com partsChange)
        } else {
          // Adicionar esta peça ao array de selecionadas
          groupMeshes.forEach((mesh) => {
            mesh.material.color.set(COLOR_SELECTED);
            mesh.material.needsUpdate = true;
          });

          const updatedSelection = [...selectedPartRef.current, groupName];
          selectedPartRef.current = updatedSelection;
          onChange([groupName]); // Envia apenas a peça clicada (compatibilidade com partsChange)
        }
      }

      resetHover();
      renderer.render(scene, camera);
    }

    // Event listeners
    container.addEventListener("mousemove", onMouseMove);
    container.addEventListener("click", onMouseClick);

    // Animation loop
    function animate() {
      requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    }
    animate();

    // Cleanup
    return () => {
      container.removeEventListener("mousemove", onMouseMove);
      container.removeEventListener("click", onMouseClick);
      if (container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }
      renderer.dispose();
    };
  }, [containerWidth, containerHeight, partsAsFilter]);

  // Atualizar seleção quando defaultValue muda externamente
  useEffect(() => {
    if (partsAsFilter) {
      // Modo multi: atualizar array de selecionadas
      // Garantir que é sempre um array
      if (Array.isArray(defaultValue)) {
        selectedPartRef.current = defaultValue;
      } else if (defaultValue) {
        selectedPartRef.current = [defaultValue];
      } else {
        selectedPartRef.current = [];
      }

      // Atualizar cores na cena
      if (sceneRef.current) {
        sceneRef.current.traverse((child) => {
          if (child.isMesh && child.userData.originalColor) {
            const normalizedMeshName = normalizeString(child.parent.name);
            const isSelected = selectedPartRef.current.some(
              part => normalizeString(part) === normalizedMeshName
            );
            if (isSelected) {
              child.material.color.set(0xa90c0c);
            } else {
              child.material.color.copy(child.userData.originalColor);
            }
          }
        });
      }
    } else {
      // Modo single: atualizar string única
      const normalizedDefault = normalizeString(defaultValue);
      const normalizedCurrent = normalizeString(selectedPartRef.current);

      if (normalizedDefault !== normalizedCurrent) {
        selectedPartRef.current = defaultValue || null;

        // Atualizar cores na cena
        if (sceneRef.current) {
          sceneRef.current.traverse((child) => {
            if (child.isMesh && child.userData.originalColor) {
              const normalizedMeshName = normalizeString(child.parent.name);
              if (defaultValue && normalizedMeshName === normalizedDefault) {
                child.material.color.set(0xa90c0c);
              } else {
                child.material.color.copy(child.userData.originalColor);
              }
            }
          });
        }
      }
    }
  }, [defaultValue, partsAsFilter]);

  return (
    <div
      ref={containerRef}
      style={{
        width: containerWidth,
        height: containerHeight,
        cursor: 'pointer'
      }}
    />
  );
};

export default CarComponent;